import { useFormik, FormikErrors } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useFieldPropsGetter } from '../form/useFieldPropsGetter';
import { useAxios } from '../hooks/useAxios';
import { useNotification } from '../notifications';

export type MirrorItem = {
  workstream: {
    id: string;
    label: string;
  };
  column: {
    id: string;
    label: string;
  };
};

export type FormData = {
  items: MirrorItem[];
};

const msgRequired = 'Field is required';

export function useMirrorAiForm(aiId: string) {
  const notify = useNotification();
  const [{ data: initialData, loading }] = useAxios({
    url: 'activity/getColumns',
    method: 'GET',
    params: {
      actId: aiId,
      json: true,
    },
  });

  const [, postMirrorItem] = useAxios(
    {
      url: 'board/changeColumn',
      method: 'POST',
      headers: {
        'content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'x-requested-with': 'XMLHttpRequest',
      },
    },
    {
      manual: true,
    }
  );

  const [, deleteMirrorItem] = useAxios(
    {
      url: 'activity/deleteAiFromBoardColumn',
      method: 'GET',
      headers: {
        'content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'x-requested-with': 'XMLHttpRequest',
      },
    },
    {
      autoCancel: false,
    }
  );

  const storeMirrorItems = useCallback(
    async (formData: FormData) => {
      try {
        const addItems = formData.items.filter(
          (item) =>
            !initialData?.existingBoardCols?.find(
              (bc) =>
                bc.object_id === item.workstream.id && bc.id === item.column.id
            )
        );
        const deleteItems = initialData?.existingBoardCols?.filter(
          (bc) =>
            !formData.items.find(
              (item) =>
                bc.object_id === item.workstream.id && bc.id === item.column.id
            )
        );
        await Promise.all([
          ...addItems?.map((item) =>
            postMirrorItem({
              data: {
                act_id: aiId,
                newColumn: item.column.id,
              },
            })
          ),
          ...deleteItems?.map((item) =>
            deleteMirrorItem({
              params: {
                act_id: aiId,
                columnId: item.id,
                tagId: item.object_id,
              },
            })
          ),
        ]);
        const report = [
          addItems.length ? `${addItems.length} added` : null,
          deleteItems.length ? `${deleteItems.length} removed` : null,
        ]
          .filter((i) => Boolean(i))
          .join(', ');
        notify.success(`Mirror items saved successfully (${report})`);
      } catch (e) {
        notify.error(
          'There was a problem while saving the data. Please try again later.'
        );
        throw e;
      }
    },
    [aiId, notify, initialData, postMirrorItem, deleteMirrorItem]
  );

  const {
    submitForm,
    setFieldValue,
    setValues,
    errors: formErrors,
    isSubmitting,
    isValidating,
    values,
  } = useFormik<FormData>({
    initialValues: {
      items: [],
    },
    validate: (data) => {
      const errors: FormikErrors<FormData> = {};
      const itemErrors = data.items
        // eslint-disable-next-line no-use-before-define
        .map((item) => validateMirrorItem(item))
        .filter((err) => Object.values(err).length > 0);
      if (itemErrors.length > 0) {
        errors.items = itemErrors;
      }
      /* data.items can be empty in case we have only one mirror item and we are removing it and that should be not an error
      if (data.items.length < 1) {
        errors.items = 'At least one item needs to be selected';
      }*/
      return errors;
    },
    onSubmit: storeMirrorItems,
  });
  const isProcessing = isSubmitting || isValidating;

  const validateMirrorItem = useCallback((val: Partial<MirrorItem>) => {
    const err: FormikErrors<MirrorItem> = {};
    if (!val.workstream) {
      err.workstream = msgRequired as any;
    }
    if (!val.column) {
      err.column = msgRequired as any;
    }
    return err;
  }, []);

  useEffect(() => {
    setValues((data) => ({
      ...data,
      items:
        initialData?.existingBoardCols?.map((boardCol) => ({
          workstream: {
            id: boardCol.object_id,
            label: boardCol.wsName,
          },
          column: {
            id: boardCol.id,
            label: boardCol.c_name,
          },
        })) ?? [],
    }));
  }, [initialData, setValues]);

  const getFieldProps = useFieldPropsGetter<FormData>({
    formErrors,
    isProcessing,
    setFieldValue,
    values,
  });

  return {
    formErrors,
    getFieldProps,
    isProcessing,
    loading,
    submitForm,
    validateMirrorItem,
    values,
  };
}
